import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import DashboardLayout from "@/components/DashboardLayout";

import PharmacistDashboard from "@/pages/pharmacist/Dashboard";
import Catalogue from "@/pages/pharmacist/Catalogue";
import OperationDetail from "@/pages/pharmacist/OperationDetail";
import Orders from "@/pages/pharmacist/Orders";
import News from "@/pages/News";
import Notifications from "@/pages/Notifications";
import ProductRequests from "@/pages/ProductRequests";
import Exchanges from "@/pages/Exchanges";

import SupplierDashboard from "@/pages/supplier/SupplierDashboard";
import SupplierOrders from "@/pages/supplier/SupplierOrders";
import SupplierOperations from "@/pages/supplier/SupplierOperations";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPharmacists from "@/pages/admin/AdminPharmacists";
import AdminOperations from "@/pages/admin/AdminOperations";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminNews from "@/pages/admin/AdminNews";
import AdminGrossistes from "@/pages/admin/AdminGrossistes";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Messages from "@/pages/Messages";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-10 w-10 rounded-full border-2 border-indigo-600 border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "supplier") return <Navigate to="/supplier" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/app" element={<HomeRedirect />} />

            {/* Pharmacist */}
            <Route element={<Protected roles={["pharmacist"]}><DashboardLayout /></Protected>}>
              <Route path="/dashboard" element={<PharmacistDashboard />} />
              <Route path="/operations" element={<Catalogue />} />
              <Route path="/operations/:id" element={<OperationDetail />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/exchanges" element={<Exchanges />} />
            </Route>

            {/* Supplier */}
            <Route element={<Protected roles={["supplier"]}><DashboardLayout /></Protected>}>
              <Route path="/supplier" element={<SupplierDashboard />} />
              <Route path="/supplier/operations" element={<SupplierOperations />} />
              <Route path="/supplier/orders" element={<SupplierOrders />} />
            </Route>

            {/* Admin */}
            <Route element={<Protected roles={["admin"]}><DashboardLayout /></Protected>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/pharmacists" element={<AdminPharmacists />} />
              <Route path="/admin/operations" element={<AdminOperations />} />
              <Route path="/admin/suppliers" element={<AdminSuppliers />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/news" element={<AdminNews />} />
              <Route path="/admin/grossistes" element={<AdminGrossistes />} />
            </Route>

            {/* Shared authenticated */}
            <Route element={<Protected roles={["pharmacist", "supplier", "admin"]}><DashboardLayout /></Protected>}>
              <Route path="/news" element={<News />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/product-requests" element={<ProductRequests />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/:tid" element={<Messages />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
